/**
 * @description Axios HTTP client for Lynx Admin portal
 *
 * Key features:
 * - Configured for same backend API as merchant frontend
 * - Credentials included for httpOnly cookie refresh tokens
 * - Rate limit retry with exponential backoff
 *
 * @dependencies
 * - axios: HTTP client
 * - config: API URL and timeout settings
 *
 * @notes
 * - withCredentials: true enables httpOnly cookie handling for token refresh
 * - 401 interceptor is in AuthContext (handles token refresh + retry)
 * - This file only handles rate limiting (429)
 */

import axios from 'axios';
import config from './config';

const apiClient = axios.create({
  baseURL: config.API_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
  timeout: config.TIMEOUT,
});

// Retry with exponential backoff for rate limits
apiClient.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (error.response?.status === 429 && !originalRequest._retry) {
      originalRequest._retry = true;
      const retryAfter = error.response.headers['retry-after'];
      const delay = retryAfter ? parseInt(retryAfter) * 1000 : 2000;
      await new Promise(resolve => setTimeout(resolve, delay));
      return apiClient(originalRequest);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
